import { Link } from 'react-router-dom';
import { Cloud, ArrowRight, Bell, CheckCircle, TrendingUp, Briefcase, Server, GitBranch } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { useState } from 'react';
import { toast } from 'sonner';
import { createLead } from '@/lib/api';

const cloudPrograms = [
  {
    name: 'AWS',
    fullName: 'Amazon Web Services',
    description: 'Master cloud computing with the world\'s most comprehensive cloud platform.',
    certifications: ['AWS Solutions Architect', 'AWS Developer', 'AWS SysOps Administrator'],
    color: 'from-orange-500 to-orange-700'
  },
  {
    name: 'Azure',
    fullName: 'Microsoft Azure',
    description: 'Build expertise in Microsoft\'s enterprise cloud platform.',
    certifications: ['Azure Administrator', 'Azure Developer', 'Azure Solutions Architect'],
    color: 'from-blue-500 to-blue-700'
  }
];

const skillTracks = [
  {
    icon: Server,
    title: 'Cloud Architecture',
    skills: ['VPC & networking design', 'Multi-AZ resiliency', 'Identity & access management', 'Cost optimization'],
  },
  {
    icon: GitBranch,
    title: 'DevOps & CI/CD',
    skills: ['Pipelines (GitHub Actions / Azure DevOps)', 'Terraform & Bicep IaC', 'Container orchestration (EKS / AKS)', 'Observability & SRE'],
  },
];

const careerOutcomes = [
  { role: 'Cloud Engineer', salary: '$95K–$140K', growth: '+18% YoY' },
  { role: 'DevOps Engineer', salary: '$105K–$160K', growth: '+22% YoY' },
  { role: 'Cloud Solutions Architect', salary: '$130K–$190K', growth: '+15% YoY' },
];

export default function CloudPage() {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);

  const handleNotify = async (e) => {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    try {
      await createLead({
        first_name: 'Interested',
        last_name: 'User',
        email,
        message: 'Interested in Cloud & DevOps training programs',
        source: 'cloud_waitlist'
      });
      toast.success('You\'re on the list! We\'ll notify you when programs launch.');
      setEmail('');
    } catch (error) {
      toast.error('Failed to subscribe. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen pt-24" data-testid="cloud-page">
      <section className="py-16 lg:py-24 bg-gradient-to-br from-blue-50 via-white to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <span className="inline-block px-4 py-2 bg-blue-100 text-blue-700 rounded-full text-sm font-medium mb-6">
              Coming Soon
            </span>
            <h1 className="text-4xl sm:text-5xl font-bold text-slate-900 mb-6">
              Cloud & DevOps Training
            </h1>
            <p className="text-lg text-slate-600 mb-8">
              Master cloud platforms and DevOps practices with our upcoming instructor-led training programs. 
              Get certified in AWS and Azure with hands-on labs and expert guidance.
            </p>
            
            <form onSubmit={handleNotify} className="flex flex-col sm:flex-row gap-3 max-w-md">
              <Input 
                type="email" 
                placeholder="Enter your email" 
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="flex-1"
                data-testid="cloud-notify-email"
              />
              <Button type="submit" className="bg-blue-600 hover:bg-blue-700 text-white rounded-full px-6" disabled={loading}>
                <Bell className="w-4 h-4 mr-2" />
                {loading ? 'Subscribing...' : 'Notify Me'}
              </Button>
            </form>
          </div>
        </div>
      </section>

      <section className="py-16 lg:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mb-10">
            <h2 className="text-3xl font-bold text-slate-900 mb-3">What you'll master</h2>
            <p className="text-slate-600">Two integrated tracks built around what hiring managers actually look for in 2026 — not just a certification badge.</p>
          </div>
          <div className="grid md:grid-cols-2 gap-6">
            {skillTracks.map((track) => (
              <Card key={track.title} className="border-slate-200">
                <CardContent className="p-7">
                  <div className="w-12 h-12 rounded-lg bg-blue-100 flex items-center justify-center mb-4">
                    <track.icon className="w-6 h-6 text-blue-700" />
                  </div>
                  <h3 className="text-xl font-bold text-slate-900 mb-3">{track.title}</h3>
                  <ul className="space-y-2">
                    {track.skills.map((s) => (
                      <li key={s} className="flex items-start text-sm text-slate-700">
                        <CheckCircle className="w-4 h-4 text-blue-600 mr-2 mt-0.5 flex-shrink-0" /> {s}
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 lg:py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between mb-10 gap-3">
            <div>
              <h2 className="text-3xl font-bold text-slate-900 mb-3">Where this takes your career</h2>
              <p className="text-slate-600 max-w-2xl">Cloud and DevOps are the highest-velocity hiring tracks in tech — here's what graduates typically target.</p>
            </div>
            <span className="inline-flex items-center text-sm text-slate-500"><TrendingUp className="w-4 h-4 mr-1 text-green-600" /> US BLS / industry reports, 2025–2026</span>
          </div>
          <div className="grid md:grid-cols-3 gap-5">
            {careerOutcomes.map((c) => (
              <Card key={c.role} className="bg-white">
                <CardContent className="p-6">
                  <Briefcase className="w-6 h-6 text-blue-600 mb-3" />
                  <div className="text-lg font-bold text-slate-900">{c.role}</div>
                  <div className="text-2xl font-extrabold text-slate-900 mt-2">{c.salary}</div>
                  <div className="text-sm text-green-700 font-medium mt-1">{c.growth}</div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl font-bold text-slate-900 mb-8">Upcoming Programs</h2>
          
          <div className="grid md:grid-cols-2 gap-8">
            {cloudPrograms.map((program) => (
              <Card key={program.name} className="border-2 border-dashed border-slate-200 bg-slate-50/50">
                <CardContent className="p-8">
                  <div className={`w-14 h-14 rounded-xl bg-gradient-to-br ${program.color} flex items-center justify-center mb-4`}>
                    <Cloud className="w-7 h-7 text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-slate-900 mb-2">{program.fullName}</h3>
                  <p className="text-slate-600 mb-4">{program.description}</p>
                  
                  <div className="mb-4">
                    <p className="text-sm font-medium text-slate-500 mb-2">Certification Tracks</p>
                    <ul className="space-y-1">
                      {program.certifications.map((cert) => (
                        <li key={cert} className="flex items-center text-sm text-slate-600">
                          <CheckCircle className="w-4 h-4 text-blue-500 mr-2" />
                          {cert}
                        </li>
                      ))}
                    </ul>
                  </div>
                  
                  <span className="inline-block px-3 py-1 bg-slate-200 text-slate-600 rounded-full text-sm">
                    Launching Q2 2026
                  </span>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 bg-slate-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-white mb-4">Want ERP Training Now?</h2>
          <p className="text-slate-400 mb-8">Our ERP programs are available today with live instruction.</p>
          <Link to="/programs">
            <Button className="bg-teal-600 hover:bg-teal-700 text-white rounded-full px-8">
              View ERP Programs <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
}
